<template>
  <transition
    enter-active-class="transition-all duration-500 ease-out"
    enter-from-class="opacity-0 translate-y-10"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-300 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-10"
  >
    <button
      v-show="isVisible"
      @click="handleClick"
      :disabled="isScrolling"
      class="group fixed bottom-8 right-8 z-50 p-4 rounded-full bg-primary text-white shadow-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-300 hover:shadow-2xl disabled:opacity-75"
      :class="{ 'animate-bounce': shouldBounce }"
      aria-label="Scroll to top"
    >
      <div class="relative">
        <!-- Arrow Icon with Animation -->
        <svg
          class="w-6 h-6 transform transition-transform duration-300 group-hover:-translate-y-1"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2.5"
            d="M5 10l7-7m0 0l7 7m-7-7v18"
          ></path>
        </svg>
        
        <!-- Ripple Effect Container -->
        <div 
          v-if="showRipple"
          class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full"
        >
          <span 
            class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-white rounded-full animate-ripple"
          ></span>
        </div>
      </div>
    </button>
  </transition>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const isVisible = ref(false)
const isScrolling = ref(false)
const showRipple = ref(false)
const shouldBounce = ref(false)
const scrollThreshold = 400 // Show button after scrolling 400px
let bounceTimeout = null
let scrollTimeout = null

const checkScroll = () => {
  isVisible.value = window.scrollY > scrollThreshold
  
  // Clear existing bounce timeout
  if (bounceTimeout) {
    clearTimeout(bounceTimeout)
  }
  
  // Add bounce effect when button first appears
  if (isVisible.value) {
    shouldBounce.value = true
    bounceTimeout = setTimeout(() => {
      shouldBounce.value = false
    }, 1000)
  }
}

const handleClick = () => {
  if (isScrolling.value) return
  
  isScrolling.value = true
  showRipple.value = true
  
  // Smooth scroll with easing
  const start = window.scrollY
  const startTime = performance.now()
  const duration = 800 // ms
  
  const easeOutCubic = t => 1 - Math.pow(1 - t, 3)
  
  const animate = currentTime => {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    
    window.scrollTo(0, start * (1 - easeOutCubic(progress)))
    
    if (progress < 1) {
      requestAnimationFrame(animate)
    } else {
      // Reset states after animation
      setTimeout(() => {
        isScrolling.value = false
        showRipple.value = false
      }, 300)
    }
  }
  
  requestAnimationFrame(animate)
}

// Throttle scroll event
const throttledCheckScroll = () => {
  if (scrollTimeout) return
  
  scrollTimeout = setTimeout(() => {
    checkScroll()
    scrollTimeout = null
  }, 100)
}

onMounted(() => {
  window.addEventListener('scroll', throttledCheckScroll)
  checkScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', throttledCheckScroll)
  if (bounceTimeout) clearTimeout(bounceTimeout)
  if (scrollTimeout) clearTimeout(scrollTimeout)
})
</script>

<style scoped>
@keyframes ripple {
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0.4;
  }
  100% {
    transform: translate(-50%, -50%) scale(2);
    opacity: 0;
  }
}

.animate-ripple {
  animation: ripple 0.8s ease-out;
}

.animate-bounce {
  animation: bounce 1s ease-in-out;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-25%);
  }
}
</style> 